import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Circles } from 'react-loading-icons';
import { Package } from 'lucide-react';

const QRBatchGenerator = ({ companyData }) => {
  // Initialize with business categories if available, else start with one empty input
  const initialCategories = companyData?.category?.map((c) =>
    typeof c === 'string' ? c : c?.title || '',
  ) || [''];

  const [categories, setCategories] = useState(
    initialCategories.length ? initialCategories : [''],
  );
  const [count, setCount] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [products, setProducts] = useState([]);
  const [businessCategories, setBusinessCategories] = useState([]);
  const [selectedProductId, setSelectedProductId] = useState('');

  const server_url = process.env.REACT_APP_API_URI;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const bid = companyData.businessId || companyData._id;
        // Fetch products
        const prodRes = await axios.post(
          `${server_url}/my-products/getMyProductsByUserId`,
          { userId: bid },
        );
        setProducts(prodRes.data || []);

        // Fetch business categories
        const catRes = await axios.get(`${server_url}/businessCategory/${bid}`);
        setBusinessCategories(catRes.data || []);
      } catch (error) {
        console.error('Error fetching batch generator data:', error);
      }
    };
    if (companyData) fetchData();
  }, [companyData, server_url]);

  const handleProductSelect = (productId) => {
    setSelectedProductId(productId);
    if (productId) {
      const product = products.find((p) => p.contentId === productId);
      if (product) {
        let hierarchy = [];

        // If product has a business category, resolve the full parent chain
        if (product.businessCategoryId) {
          const resolveParents = (catId) => {
            const cat = businessCategories.find((c) => c._id === catId);
            if (cat) {
              if (cat.parentId) resolveParents(cat.parentId);
              hierarchy.push(cat.name);
            }
          };

          const startCatId =
            typeof product.businessCategoryId === 'string'
              ? product.businessCategoryId
              : product.businessCategoryId._id;

          resolveParents(startCatId);
        }

        // Always add the product name as the final level
        hierarchy.push(product.contentName);

        // Update categories state (Level 1, Level 2, etc.)
        setCategories(hierarchy.length ? hierarchy : [product.contentName]);
      }
    }
  };

  const handleCategoryChange = (index, value) => {
    const newCategories = [...categories];
    newCategories[index] = value;
    setCategories(newCategories);
  };

  const addCategoryField = () => {
    setCategories([...categories, '']);
  };

  const removeCategoryField = (index) => {
    const newCategories = categories.filter((_, i) => i !== index);
    setCategories(newCategories);
  };

  const handleGenerateAndDownload = async () => {
    setLoading(true);
    setError(null);
    try {
      // Filter out empty responses to send clean arrays
      const validCategories = categories
        .map((c) => (typeof c === 'string' ? c : c?.title || ''))
        .filter((c) => c.trim() !== '');

      const response = await axios.post(
        `${server_url}/qr/generate-batch`,
        {
          businessId: companyData._id,
          categories: validCategories,
          count: parseInt(count, 10),
          productId: selectedProductId || null,
        },
        {
          responseType: 'blob', // Important for handling binary data like ZIP
        },
      );

      // Create a URL for the blob and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        `qrcodes_${companyData._id}_${Date.now()}.zip`,
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);

      // Clear the form back to its default clean state
      setCategories(initialCategories.length ? initialCategories : ['']);
      setCount(10);
    } catch (err) {
      console.error(err);
      setError('Failed to generate QR codes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-8 p-6 bg-gray-800 rounded-xl">
      <h2 className="text-2xl font-semibold text-center text-white mb-4">
        QR Batch Generator (For Verified Purchases)
      </h2>
      <p className="text-gray-400 text-center mb-6">
        Generate unique QR codes to prove purchases. Print them on receipts or
        packaging.
      </p>

      {error && <p className="text-red-500 text-center mb-4">{error}</p>}

      <div className="max-w-xl mx-auto mb-8 bg-slate-700/30 p-5 rounded-2xl border border-slate-600/50">
        <label className="flex items-center gap-2 text-slate-300 text-sm font-bold uppercase tracking-widest mb-3">
          <Package className="w-4 h-4 text-blue-400" />
          Assign to Product (Optional)
        </label>
        <select
          className="w-full p-3 bg-gray-900 text-white rounded-xl border border-gray-700 focus:outline-none focus:border-blue-500 transition-all font-medium"
          value={selectedProductId}
          onChange={(e) => handleProductSelect(e.target.value)}
        >
          <option value="">General (No specific product)</option>
          {products.map((product) => (
            <option key={product._id} value={product.contentId}>
              {product.contentName}
            </option>
          ))}
        </select>
        <p className="text-[10px] text-slate-500 mt-2 font-medium">
          If selected, these QR codes will specifically link to the rating page
          for this product.
        </p>
      </div>

      <div className="max-w-xl mx-auto space-y-4">
        <div>
          <label className="block text-gray-400 mb-2">
            Product Information & Categories
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {categories.map((category, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="text"
                  className="flex-1 p-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500 min-w-0"
                  value={category}
                  onChange={(e) => handleCategoryChange(index, e.target.value)}
                  placeholder={`Category Level ${index + 1} (e.g. ${index === 0 ? 'Beverages' : index === 1 ? 'Espresso' : 'Detail'})`}
                />
                {categories.length > 1 && (
                  <button
                    onClick={() => removeCategoryField(index)}
                    className="p-2 text-red-400 hover:text-red-300 transition-colors flex-shrink-0"
                    title="Remove category"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-center mt-5">
            <button
              onClick={addCategoryField}
              className="w-full md:w-auto px-6 py-3 bg-gray-700/30 hover:bg-gray-700 border border-dashed border-gray-500 hover:border-blue-400 text-gray-400 hover:text-white rounded-xl transition-all duration-300 shadow-sm hover:shadow-md flex items-center justify-center gap-3 group"
            >
              <div className="bg-gray-600 group-hover:bg-blue-500 text-white rounded-full p-1.5 transition-colors duration-300">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <span className="font-medium tracking-wide">
                Add Another Detail Level
              </span>
            </button>
          </div>
        </div>

        <div>
          <label className="block text-gray-400 mb-1">
            Number of QR Codes to Generate
          </label>
          <input
            type="number"
            className="w-full p-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:outline-none focus:border-blue-500"
            value={count}
            onChange={(e) => setCount(e.target.value)}
            min="1"
            max="1000"
          />
        </div>
      </div>

      <div className="flex justify-center mt-8">
        <button
          onClick={handleGenerateAndDownload}
          disabled={
            loading ||
            count < 1 ||
            categories.length === 0 ||
            categories[0] === ''
          }
          className={`px-8 py-3 rounded-xl font-semibold shadow-lg transition-all duration-300 ${
            loading ||
            count < 1 ||
            categories.length === 0 ||
            categories[0] === ''
              ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
              : 'bg-gradient-to-r from-[#F2994A] to-[#F2C94C] text-[#0E1834] hover:shadow-xl hover:-translate-y-0.5'
          }`}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <Circles height="20" width="20" /> Generating...
            </span>
          ) : (
            'Generate & Download ZIP'
          )}
        </button>
      </div>
    </div>
  );
};

export default QRBatchGenerator;
